/**
 * Lightweight i18n helper for Boomfection frontend
 *
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */

const SUPPORTED_LOCALES = ['de', 'en'];
const DEFAULT_LOCALE = 'de';

let currentLocale = DEFAULT_LOCALE;
let translations = {};
let initialized = false;

/**
 * Detects the preferred locale from localStorage or browser settings
 * @returns {string}
 */
export function detectLocale() {
    // 1. User preference in localStorage
    const saved = localStorage.getItem('boomfection_locale');
    if (saved && SUPPORTED_LOCALES.includes(saved)) return saved;

    // 2. Browser language
    const browserLang = (navigator.language || '').split('-')[0].toLowerCase();
    if (SUPPORTED_LOCALES.includes(browserLang)) return browserLang;

    return DEFAULT_LOCALE;
}

/**
 * Switch locale at runtime
 * @param {string} locale
 * @returns {Promise<void>}
 */
export async function setLocale(locale) {
    if (!SUPPORTED_LOCALES.includes(locale)) return;
    localStorage.setItem('boomfection_locale', locale);
    await initI18n(locale);
}

/**
 * Returns the list of supported locales
 * @returns {string[]}
 */
export function getSupportedLocales() {
    return [...SUPPORTED_LOCALES];
}

/**
 * Loads a locale file and initializes translations
 * @param {string} locale - Locale code (e.g. 'de', 'en')
 * @returns {Promise<void>}
 */
export async function initI18n(locale = DEFAULT_LOCALE) {
    currentLocale = locale;
    try {
        const response = await fetch(`/locales/${locale}.json`);
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        translations = await response.json();
        initialized = true;
    } catch {
        console.warn(`[i18n] Failed to load locale "${locale}", using fallback keys`);
        translations = {};
        initialized = false;
    }
}

/**
 * Translates a key with optional parameter interpolation
 * Supports dot notation: 'game.yourTurn' resolves to translations.game.yourTurn
 * Replaces {{param}} placeholders with provided values
 *
 * @param {string} key - Dot-notated translation key
 * @param {Object} [params={}] - Interpolation parameters
 * @returns {string} Translated string or key as fallback
 */
export function t(key, params = {}) {
    const keys = key.split('.');
    let value = translations;

    for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
            value = value[k];
        } else {
            return key;
        }
    }

    if (typeof value !== 'string') {
        return key;
    }

    if (params && Object.keys(params).length > 0) {
        return value.replace(/\{\{(\w+)\}\}/g, (_, name) => {
            return name in params ? params[name] : `{{${name}}}`;
        });
    }

    return value;
}

/**
 * Returns the currently active locale
 * @returns {string}
 */
export function getLocale() {
    return currentLocale;
}

/**
 * Returns whether i18n has been successfully initialized
 * @returns {boolean}
 */
export function isInitialized() {
    return initialized;
}

/**
 * Apply translations to all elements with data-i18n attributes
 * - data-i18n="key" → sets textContent
 * - data-i18n-placeholder="key" → sets placeholder
 * - data-i18n-title="key" → sets title attribute
 * - data-i18n-aria-label="key" → sets aria-label
 */
export function applyI18nToDOM() {
    const attrs = [
        { attr: 'data-i18n', prop: 'textContent' },
        { attr: 'data-i18n-placeholder', prop: 'placeholder' },
        { attr: 'data-i18n-title', prop: 'title' },
        { attr: 'data-i18n-aria-label', setter: (el, val) => el.setAttribute('aria-label', val) }
    ];

    for (const { attr, prop, setter } of attrs) {
        document.querySelectorAll(`[${attr}]`).forEach((el) => {
            const key = el.getAttribute(attr);
            const val = t(key);
            if (val && val !== key) {
                if (setter) setter(el, val);
                else el[prop] = val;
            }
        });
    }
}
